import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import CustomPagination from '../../common/MotifPagination';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import SecuritiesSnapshotCellRenderer from '../shared/customRenderers/SecuritiesSnapshotCellRenderer';
import {
  handleFilterChanged,
  handleFirstDataRendered,
  handleGridReady,
  tableHeaderClass,
  handleItemsPerPageChange,
} from '../../common/AgGridEventHandlers';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate.js';
import { MotifTable } from '@ey-xd/motif-react';
import CustomHeaderWithBadge from '../shared/customRenderers/CustomHeaderWithBadge';

const editSecuritiesSnapshotTable = ({
  securitiesSnapshots,
  showForm,
  handleEditButtonClick,
  canEdit,
  hideZeroBalanceRows,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  const FormattedCurrency = useCallback(({ value }) => {
    return Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }, []);

  const shouldDisplayBadge = useCallback((params) => {
    return !!(params.data.hasIncompleteSecuritySnapshot === true);
  }, []);
  const columnDefs = useMemo(() => {
    return [
      {
        field: 'securityName',
        headerName: 'Security Name',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 250,
        cellRenderer: CustomHeaderWithBadge,
        cellRendererParams: (params) => ({
          displayName: params.data.securityName,
          condition: shouldDisplayBadge(params),
        }),
        headerComponentParams: {
          template: CustomHeaderTemplate('securityName'),
        },
      },
      {
        field: 'reitOrPropertyName',
        headerName: 'REIT or Property Name',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 350,
        headerComponentParams: {
          template: CustomHeaderTemplate('REITorPropertyName'),
        },
      },
      {
        field: 'reitSuiteEntityIDForREIT',
        headerName: 'REITSuite Entity ID for REIT',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 350,
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDforREIT'),
        },
      },
      {
        field: 'reitSuiteEntityIDForProperty',
        headerName: 'REITSuite Entity ID for Property',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 350,
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDforProperty'),
        },
      },
      {
        field: 'clientAccountCode',
        headerName: 'Client Account Number',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 250,
        headerComponentParams: {
          template: CustomHeaderTemplate('clientAccountCode'),
        },
      },
      {
        field: 'reitOwnedSharesValue',
        headerName: 'REIT Owned Shares Value',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 270,
        cellRenderer: SecuritiesSnapshotCellRenderer,
        cellRendererParams: { fieldName: 'reitOwnedSharesValue' },
        headerComponentParams: {
          template: CustomHeaderTemplate('reitOwnedSharesValue'),
        },
        valueGetter: (params) => {
          return FormattedCurrency({ value: params.data.reitOwnedSharesValue });
        },
      },
      {
        field: 'allSharesFromIssuerValue',
        headerName: 'All Shares from Issuer Value',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 285,
        headerComponentParams: {
          template: CustomHeaderTemplate('allSharesFromIssuerValue'),
        },
        cellRenderer: SecuritiesSnapshotCellRenderer,
        cellRendererParams: { fieldName: 'allSharesFromIssuerValue' },
        valueGetter: (params) => {
          return params.data.isTenPercentValueTestDisabled === true
            ? 'N/A'
            : FormattedCurrency({
                value: params.data.allSharesFromIssuerValue,
              });
        },
      },
      {
        field: 'percentOfVote',
        headerName: 'Percent of Vote',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 200,
        headerComponentParams: {
          template: CustomHeaderTemplate('percentOfVote'),
        },
        cellRenderer: SecuritiesSnapshotCellRenderer,
        cellRendererParams: { fieldName: 'percentOfVote' },
        valueGetter: (params) => {
          const percentage = params.data && params.data.percentOfVote;
          return percentage
            ? ((percentage * 1000.0) / 100000.0).toLocaleString(undefined, {
                style: 'percent',
                minimumFractionDigits: 4,
                maximumFractionDigits: 10,
              })
            : '';
        },
      },
      {
        field: 'action',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        showRowGroup: false,
        minWidth: 150,
        rowGroup: false,
        cellRenderer: SecuritiesSnapshotCellRenderer,
        cellRendererParams: {
          fieldName: 'actions',
          canEdit,
          handleEditButtonClick,
        },
      },
    ];
  }, [FormattedCurrency, shouldDisplayBadge, canEdit, handleEditButtonClick]);

  //Handle deselect all event
  const handleDeselectAll = useCallback((val) => {
    // set current page and total pages to 0
    setCurrentPage(0);
    setTotalPages(0);
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  useEffect(() => {
    if (rowData.length > 0) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    let aggregatedSnapshots = securitiesSnapshots.reduce((acc, snapshot) => {
      const key = `${snapshot.securitiesSnapshotID}`;
      if (!acc[key]) {
        acc[key] = {
          ...snapshot,
          clientAccountCode: '',
        };
      }
      acc[key].reitOwnedSharesValue =
        snapshot.reitOwnedSharesValue < 0 ? 0 : acc[key].reitOwnedSharesValue;
      acc[key].clientAccountCode = acc[key].clientAccountCode
        ? `${acc[key].clientAccountCode}; ${snapshot.clientAccountCode}`
        : snapshot.clientAccountCode;
      return acc;
    }, {});

    const aggregatedSecuritiesSnapshots = Object.values(aggregatedSnapshots);
    // Sort to match original order
    const positionMap = new Map(
      securitiesSnapshots.map((s, i) => [s.securitiesSnapshotID, i])
    );
    aggregatedSecuritiesSnapshots.sort(
      (a, b) =>
        (positionMap.get(a.securitiesSnapshotID) ?? 0) -
        (positionMap.get(b.securitiesSnapshotID) ?? 0)
    );
    setRowData(aggregatedSecuritiesSnapshots);
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [securitiesSnapshots]);

  // Force refresh securityName cells so the incomplete security badge re-evaluates
  const handleRowDataUpdated = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({ force: true, columns: ['securityName'] });
    }
  }, []);

  const rowHeight = 76;
  const headerHeight = 45;
  const totalHeight = 5 * rowHeight + headerHeight; // set 5 rows as default

  const onCellKeyDown = useCallback(
    (event) => {
      if (event.event.key === 'Enter' || event.event.key === ' ') {
        if (event.colDef.field == 'action') {
          handleEditButtonClick(event.data);
        }
      }
    },
    [handleEditButtonClick]
  );
  return (
    <>
      {(securitiesSnapshots == null || securitiesSnapshots.length === 0) &&
        hideZeroBalanceRows === true && (
          <div className="mt-2">
            No System Generated Securities Snapshots with a balance are found
            for this report period.
          </div>
        )}
      {(securitiesSnapshots == null || securitiesSnapshots.length === 0) &&
        hideZeroBalanceRows === false && (
          <div className="mt-2">
            No Securities Snapshot found for this report period.
          </div>
        )}
      {securitiesSnapshots && securitiesSnapshots.length > 0 && (
        <div>
          <div className="row mt-2">
            <div className="col">
              <div className="ag-theme-quartz motif-table table-bd-full-height-width">
                <MotifTable
                  zebra={true}
                  compact={true}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  accentedSort={true}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  onGridReady={(event) =>
                    handleGridReady(
                      event,
                      gridRef,
                      setCurrentPage,
                      setItemsPerPage,
                      setTotalPages,
                      'REITSuiteSecuritySnapshotColumnState',
                      handleDeselectAll
                    )
                  }
                  onFirstDataRendered={handleFirstDataRendered(gridRef)}
                  ref={gridRef}
                  pagination={true}
                  suppressPaginationPanel={true}
                  paginationPageSize={100}
                  groupDisplayType={'custom'}
                  groupSelectsChildren={true}
                  groupDefaultExpanded={0}
                  onFilterChanged={handleFilterChanged(
                    gridRef,
                    currentPage,
                    setCurrentPage,
                    setTotalPages
                  )}
                  data-testid="divSecuritySnapShotListGrid"
                  deltaRowDataMode={true}
                  getRowId={(data) => data.data.securitiesSnapshotID}
                  onRowDataUpdated={handleRowDataUpdated}
                  onCellKeyDown={onCellKeyDown}
                />
                <div className="mt-3">
                  <span className="right mb-3">
                    <CustomPagination
                      currentPage={currentPage}
                      handlePageChange={handlePageChange}
                      totalPages={totalPages}
                      handleItemsPerPageChange={handleItemsPerPageChange}
                      gridRef={gridRef}
                      setCurrentPage={setCurrentPage}
                      setItemsPerPage={setItemsPerPage}
                      setTotalPages={setTotalPages}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

editSecuritiesSnapshotTable.propTypes = {
  securitiesSnapshots: PropTypes.arrayOf(
    PropTypes.shape({
      allSharesFromIssuerValue: PropTypes.number,
      class: PropTypes.string,
      securitiesSnapshotID: PropTypes.number,
      issuer: PropTypes.string,
      securityName: PropTypes.string,
      reitOwnedSharesValue: PropTypes.number,
      percentOfVote: PropTypes.number,
      reportPeriodID: PropTypes.number,
    })
  ),
  showForm: PropTypes.bool,
  handleEditButtonClick: PropTypes.func,
  handleDeleteButtonClick: PropTypes.func,
  canEdit: PropTypes.bool,
  hideZeroBalanceRows: PropTypes.bool,
};

export default editSecuritiesSnapshotTable;
